import { ProductAgreementDTO } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    AgreementsActionFailure,
    DeleteAgreement,
    LoadAgreementsByProduct,
    LoadAgreementsByProductFailure,
    LoadAgreementsByProductSuccess,
    UpsertProductAgreement
} from '../actions/agreement.actions';
import { AppState } from '../store';

export interface AgreementState extends EntityState<ProductAgreementDTO> {
    loading: boolean;
    actionLoading: boolean;
    error: any;
    loadedProducts: string[];
}

function selectAgreementId(a: ProductAgreementDTO): string {
    return a.productAgreementId;
}

function sortAgreements(a: ProductAgreementDTO, b: ProductAgreementDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const agreementAdapter: EntityAdapter<ProductAgreementDTO> = createEntityAdapter<ProductAgreementDTO>({
    selectId: selectAgreementId,
    sortComparer: sortAgreements
});

export const initialState: AgreementState = agreementAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    error: undefined,
    loadedProducts: []
});

export const agreementReducer = createReducer(
    initialState,
    on(LoadAgreementsByProduct, (state) => ({ ...state, loading: true })),
    on(LoadAgreementsByProductSuccess, (state, { agreements, productId }) =>
        agreementAdapter.upsertMany(
            agreements || [],
            agreementAdapter.removeMany((agreement) => agreement.productId === productId, {
                ...state,
                loading: false,
                actionLoading: false,
                loadedProducts: [...state.loadedProducts, productId]
            })
        )
    ),
    on(LoadAgreementsByProductFailure, (state, error) => ({ ...state, loading: false, error })),

    on(UpsertProductAgreement, (state) => ({ ...state, actionLoading: true })),
    on(DeleteAgreement, (state) => ({ ...state, actionLoading: true })),

    on(AgreementsActionFailure, (state, error) => ({ ...state, actionLoading: false, error }))
);

const { selectAll } = agreementAdapter.getSelectors();

export const agreementState = (state: AppState) => state.agreementState;
export const allAgreements = createSelector(agreementState, selectAll);
export const agreementsByProduct = (productId: string) =>
    createSelector(allAgreements, (agreements) => agreements.filter((elem) => elem.productId === productId));
export const agreementsLoadedByProduct = (productId: string) =>
    createSelector(agreementState, (state) => state.loadedProducts.includes(productId));
export const agreementsLoading = createSelector(agreementState, (state) => state.loading);
export const agreementsActionLoading = createSelector(agreementState, (state) => state.actionLoading);
